import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  shareReplay
} from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { ResourceLinkType } from '@bling-fe/shared/services';
import { GroupRepository } from '../repositories/group.repository';
import { ArticleRepository } from '../repositories/article.repository';
import { DocumentRepository } from '../repositories/document.repository';
import { getCurrentUser } from '../../core/states/user-state/user.selectors';

/**
 * Resolves an internal-link resource ({ type, id }) to its display name — a
 * group's title, or a post/event title — so a link can show "Wohnbaupolitik"
 * instead of the generic "Gruppe".
 *
 * Resolution is per viewer (the current user's own API calls), so it inherently
 * respects authorization: a resource the user can't access returns 403/404,
 * which we swallow and resolve to null — the caller then keeps the generic
 * label. Results are cached and shared per `type:id` so the same resource is
 * fetched once regardless of how many links reference it.
 *
 * The cache is cleared whenever the current user changes, so a title resolved
 * for one user (e.g. a private group's name) is never served to the next user
 * after a logout/login without a full page reload.
 */
@Injectable({ providedIn: 'root' })
export class InternalLinkTitleService {
  private readonly cache = new Map<string, Observable<string | null>>();

  constructor(
    private groupRepository: GroupRepository,
    private articleRepository: ArticleRepository,
    private documentRepository: DocumentRepository,
    private store: Store
  ) {
    this.store
      .pipe(
        select(getCurrentUser),
        map(user => user?.id ?? null),
        distinctUntilChanged()
      )
      .subscribe(() => this.cache.clear());
  }

  resolve(type: ResourceLinkType, id: number): Observable<string | null> {
    const key = `${type}:${id}`;
    const cached = this.cache.get(key);
    if (cached) return cached;

    const title$ = this.fetchTitle(type, id).pipe(
      // Swallow 403/404 (and anything else) so an inaccessible or deleted
      // resource silently falls back to the generic label instead of surfacing
      // the global error toast.
      catchError(() => of(null)),
      shareReplay(1)
    );
    this.cache.set(key, title$);
    return title$;
  }

  private fetchTitle(
    type: ResourceLinkType,
    id: number
  ): Observable<string | null> {
    return this.repositoryFor(type)
      .findById(id)
      .pipe(map(resource => resource?.title?.trim() || null));
  }

  // Posts and events are both articles (same endpoint); groups and documents have their own.
  private repositoryFor(type: ResourceLinkType): {
    findById(id: number): Observable<{ title?: string }>;
  } {
    switch (type) {
      case 'group':
        return this.groupRepository;
      case 'document':
        return this.documentRepository;
      default:
        return this.articleRepository;
    }
  }
}
